import React, { useEffect, useState } from "react";
import { fetchQuery, QueryRenderer } from "react-relay";

import moment from "moment";
import { graphql } from "relay-runtime";

import { DueDate } from "~/common";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import Avatar from "~/ui/legacy/Avatar";
import LoadingSpinner from "~/ui/LoadingSpinner";
import toasty from "~/ui/toast";

import type { CreateOrImportProps } from "../actionItemIntegration/actionItemLinkingModal";
import { ListSelector } from "./selectors/MicrosoftTodoListSelector";
import { TaskSelector } from "./selectors/MicrosoftTodoTaskSelector";
import { MicrosoftTodoActionItemIntegrationLinkWidget, microsoftTodoSpecificDataNodeTypename } from "./Widget";

import type { MicrosoftTodoImportExistingTicketPaneMutation } from "~/__generated__/MicrosoftTodoImportExistingTicketPaneMutation.graphql";
import type {
	MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery,
	MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery$data,
} from "~/__generated__/MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery.graphql";
import type { MicrosoftTodoImportExistingTicketPaneTaskQuery } from "~/__generated__/MicrosoftTodoImportExistingTicketPaneTaskQuery.graphql";

import commonStyles from "~/integrations/actionItemIntegration/css/styles.module.css";

import styles from "./css/styles.module.css";

const taskQuery = graphql`
	query MicrosoftTodoImportExistingTicketPaneTaskQuery($listId: String!, $taskId: String!) {
		viewer {
			integrations {
				microsoftTodo {
					task(listId: $listId, taskId: $taskId) {
						name
						externalId
						listId
						listName
					}
				}
			}
		}
	}
`;

const taskPreviewQuery = graphql`
	query MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery($taskId: String!, $listId: String!) {
		viewer {
			integrations {
				microsoftTodo {
					taskPreview(taskId: $taskId, listId: $listId) {
						title
						externalId
						due
						done
						assignee {
							id
							fullName
							image
						}
					}
				}
			}
		}
	}
`;

export type TaskType = NonNullable<
	MicrosoftTodoImportExistingTicketPaneTaskQuery["response"]["viewer"]["integrations"]["microsoftTodo"]
>["task"];

function getTaskById(taskId: string, listId: string) {
	return fetchQuery<MicrosoftTodoImportExistingTicketPaneTaskQuery>(environment, taskQuery, {
		taskId: taskId,
		listId: listId,
	}).toPromise();
}

const importExternalTicket = graphql`
	mutation MicrosoftTodoImportExistingTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$initialText: String!
		$destinationInfo: JSON!
	) {
		loadActionItemFromIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				initialText: $initialText
				destinationInfo: $destinationInfo
				integrationProvider: MICROSOFT_TODO
			}
		) {
			clientMutationId
		}
	}
`;

function importActionItem(
	blockKey: string,
	noteId: string,
	taskId: string,
	listId: string,
	listName: string,
	initialText: string,
) {
	return commitMutationPromise<MicrosoftTodoImportExistingTicketPaneMutation>(environment, {
		mutation: importExternalTicket,
		variables: {
			blockKey: blockKey,
			noteId: noteId,
			initialText: initialText,
			destinationInfo: {
				/* eslint-disable camelcase */
				task_id: taskId,
				list_id: listId,
				list_name: listName,
				/* eslint-enable camelcase */
			},
		},
	});
}

export function ImportExistingTicketPane({
	setCanSubmit,
	registerSubmit,
	blockKey,
	noteId,
	initialText,
}: CreateOrImportProps) {
	const [taskId, setTaskId] = useState<string>("");
	const [task, setTask] = useState<TaskType>();
	const [listId, setListId] = useState<string>("");
	const [_listName, setListName] = useState<string>();
	const debouncedTaskId = useDebouncedValue(taskId, 300);

	useEffect(() => {
		if (debouncedTaskId === "") return;
		getTaskById(debouncedTaskId, listId).then(result => {
			const task = result?.viewer.integrations.microsoftTodo?.task;
			if (task) setTask(task);
		});
	}, [debouncedTaskId, listId]);

	const submit = React.useCallback(async () => {
		if (!task) {
			throw new Error("Invalid state, submit should not be available if item is null");
		}

		try {
			await importActionItem(blockKey, noteId, task.externalId, task.listId, task.listName, initialText);
		} catch (e) {
			if (Array.isArray(e)) {
				for (let exception of e) {
					if ("message" in exception) toasty.error(exception.message);
				}
			}
		}
		return true;
	}, [blockKey, initialText, noteId, task]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		// check state is valid to be submitted on state change.
		setCanSubmit(!!task && taskId === task.externalId);
	}, [setCanSubmit, task, taskId]);

	const renderPreview = React.useCallback(
		({ props }: { props: MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery$data | null }) => {
			if (!props) {
				return (
					<div className={styles.spinnerBody}>
						<LoadingSpinner dimmer={false} />
					</div>
				);
			}

			const previewTask = props.viewer.integrations.microsoftTodo?.taskPreview;
			if (!previewTask || !task) {
				return null;
			}

			return (
				<div>
					<div className={styles.label}>Preview</div>

					<div className={commonStyles.preview}>
						<div className={commonStyles.row}>
							<div className={commonStyles.items}>
								<div className={commonStyles.item}>
									<div className={commonStyles.actionCheckbox} />
								</div>

								<div className={commonStyles.item}>{previewTask.title}</div>
							</div>

							{previewTask.assignee || previewTask.due ? (
								<div className={commonStyles.items}>
									{previewTask.due && (
										<div className={commonStyles.item}>
											<DueDate as="span" styled short date={moment(previewTask.due as string).format("YYYY-MM-DD")} />
										</div>
									)}

									{previewTask.assignee && (
										<div className={commonStyles.item}>
											<Avatar user={previewTask.assignee} link={false} width="24px" />
										</div>
									)}
								</div>
							) : null}
						</div>

						<div className={commonStyles.pillWrapper}>
							<MicrosoftTodoActionItemIntegrationLinkWidget
								actionItemLink={{
									integrationData: {
										__typename: microsoftTodoSpecificDataNodeTypename,
										externalTaskId: previewTask.externalId || "",
										externalListName: task.listName,
										externalListId: task.listId,
										status: "",
										microsoftTodoUser: {
											isConnected: true,
										},
									},
									syncStatus: "DONE_SYNCING_EXTERNALLY",
								}}
							/>
						</div>
					</div>
				</div>
			);
		},
		[task],
	);

	return (
		<div className={styles.container}>
			<label className={styles.list}>
				<div className={styles.label}>List</div>
				<ListSelector listId={listId} setListId={setListId} setListName={setListName} />
			</label>
			{listId && (
				<label className={styles.tasks}>
					<div className={styles.label}>Task</div>
					<TaskSelector taskId={taskId} listId={listId} setTaskId={setTaskId} />
				</label>
			)}
			{taskId && (
				<div className={styles.previewWrapper}>
					<QueryRenderer<MicrosoftTodoImportExistingTicketPaneTaskPreviewQuery>
						environment={environment}
						query={taskPreviewQuery}
						variables={{
							taskId: taskId,
							listId: listId,
						}}
						render={renderPreview}
					/>
				</div>
			)}
		</div>
	);
}
